import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const PurchaseReceiptContext = createContext();

export const PurchaseReceiptProvider = ({ children }) => {
  const [purchaseReceipts, setPurchaseReceipts] = useState({
    data: [],
    meta: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
    status: "all", // all, active, cancelled
    date_from: "",
    date_to: "",
  });

  const { endpoint, queryString } = (() => {
    const baseQuery = {
      search: filterOptions.search || "",
      page: filterOptions.currentPage,
      limit: 10,
    };

    // Only include status if it's not "all"
    if (filterOptions.status && filterOptions.status !== "all") {
      baseQuery.status = filterOptions.status;
    }

    // Include date range if provided
    if (filterOptions.date_from) {
      baseQuery.date_from = filterOptions.date_from;
    }
    if (filterOptions.date_to) {
      baseQuery.date_to = filterOptions.date_to;
    }

    return {
      endpoint: "/purchase-receipts",
      queryString: buildQueryString(baseQuery),
    };
  })();

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const handleStatusFilter = (status) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      status: status,
    }));
  };

  const handleDateFromFilter = (dateFrom) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      date_from: dateFrom,
    }));
  };

  const handleDateToFilter = (dateTo) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      date_to: dateTo,
    }));
  };

  const fetchPurchaseReceipts = useCallback(async () => {
    setPurchaseReceipts((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`${endpoint}${queryString}`);
      console.log("Purchase Receipts API Response:", response);

      if (response.success) {
        setPurchaseReceipts({
          data: response.data,
          meta: response.meta || response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.meta?.last_page || response.pagination?.last_page,
        }));
      } else {
        throw new Error(response.message || "Failed to fetch purchase receipts");
      }
    } catch (error) {
      console.error("Error fetching purchase receipts:", error);
      setPurchaseReceipts((prev) => ({
        ...prev,
        error: error.message || "Failed to fetch purchase receipts",
        loading: false,
      }));
    }
  }, [endpoint, queryString]);

  useEffect(() => {
    fetchPurchaseReceipts();
  }, [fetchPurchaseReceipts]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = purchaseReceipts.meta?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <PurchaseReceiptContext.Provider
      value={{
        purchaseReceipts,
        filterOptions,
        handleSearch,
        handleStatusFilter,
        handleDateFromFilter,
        handleDateToFilter,
        refetch: fetchPurchaseReceipts,
        Pagination,
      }}
    >
      {children}
    </PurchaseReceiptContext.Provider>
  );
};

export const usePurchaseReceipt = () => {
  const context = useContext(PurchaseReceiptContext);
  if (!context) {
    throw new Error("usePurchaseReceipt must be used within PurchaseReceiptProvider");
  }
  return context;
};
